import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../../../../shared';
import { TEXT, PLACEHOLDER } from '../../../../../constants';
import { dateFormatChanger, showDate } from '../../../../../utils';

/**
 * PackingFieldsGroup - Reusable component for packing-related form fields
 * Only used in EditBatch for managing pack quantities and dates
 * Memoized to prevent unnecessary re-renders when props haven't changed
 *
 * NOTE: Package quantity is always disabled and defaults to sew quantity.
 * Users cannot change the package quantity - it always equals the sew quantity.
 *
 * @param {string} mode - 'edit' | 'readonly'
 * @param {Object} values - { pckQty, pckDate } - pckQty should equal sew_qty
 * @param {Function} onPackQtyChange - Handler for pack qty change (not used since field is disabled)
 * @param {Function} onPackDateChange - Handler for pack date change
 * @param {Object} refs - { pckQuantity }
 * @param {Object} batchData - Existing batch data
 * @param {string} minDate - Minimum date allowed (sew date)
 * @param {boolean} isEditable - Whether fields are editable
 */
const PackingFieldsGroupComponent = ({
  mode = 'readonly',
  values,
  onPackQtyChange,
  onPackDateChange,
  refs,
  batchData,
  minDate,
  isEditable = false
}) => {
  /**
   * Only show input fields if:
   * - Mode is 'edit' (not readonly)
   * - AND isEditable=true (lot not in dyeing state)
   *
   * Business Rule: Packing can only be done after sewing is complete
   * If lot is in dyeing state, fields are readonly to prevent data conflicts
   */
  const shouldShowInput = mode === 'edit' && isEditable;

  return (
    <>
      {/* Package Quantity */}
      <div className="col-4">{TEXT.PACKAGE_QTY}</div>
      {shouldShowInput ? (
        <Input
          ref={refs?.pckQuantity}
          type="number"
          value={values.pckQty}
          className="col-8"
          placeholder={batchData?.sew_qty}
          /**
           * Always disabled - pack quantity MUST equal sew quantity
           * Business Rule: You cannot pack more or less than what was sewn
           * This field is auto-populated from sew_qty and cannot be modified
           * to maintain data consistency across the manufacturing pipeline
           */
          disabled={true}
          onChange={(e) => {
            // Let the hook handle validation to avoid double validation
            const inputValue = e.target.value === '' ? '' : +e.target.value;
            onPackQtyChange(inputValue);
          }}
          // Prevent mouse wheel from changing number input
          onWheel={(e) => e.target.blur()}
        />
      ) : (
        <div className="col-8">{batchData?.pack_qty || batchData?.sew_qty || 0}</div>
      )}

      {/* Package Date */}
      <div className="col-4">{TEXT.PACKAGE_DATE}</div>
      {shouldShowInput ? (
        <Input
          type="date"
          className="col-8"
          placeholder={PLACEHOLDER.ENTER_DATE}
          value={showDate(values.pckDate)}
          onChange={(e) => onPackDateChange(e.target.value)}
          /**
           * minDate enforces chronological order: pack date cannot be before sew date
           * This maintains logical workflow sequence in the manufacturing process
           */
          min={minDate}
          max={showDate()}
          onKeyDown={(e) => e.preventDefault()}
        />
      ) : (
        <div className="col-8">{dateFormatChanger(values?.pckDate)}</div>
      )}
    </>
  );
};

PackingFieldsGroupComponent.propTypes = {
  mode: PropTypes.oneOf(['edit', 'readonly']),
  values: PropTypes.shape({
    pckQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Backend sometimes returns false/null for dates instead of strings
    pckDate: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }),
  onPackQtyChange: PropTypes.func,
  onPackDateChange: PropTypes.func,
  refs: PropTypes.shape({
    pckQuantity: PropTypes.object
  }),
  batchData: PropTypes.object,
  minDate: PropTypes.string,
  isEditable: PropTypes.bool
};

export const PackingFieldsGroup = memo(PackingFieldsGroupComponent);
